import { t as __exportAll } from "./chunk-rolldown-runtime.mjs";
import { n as otel_exports } from "./chunk-extensions.mjs";
import { t as stubExtension } from "./chunk-stub.mjs";
import { log } from "@dxos/log";
import { gzip, isNode } from "@dxos/util";
import * as Effect from "effect/Effect";
import { getEnvString } from "@dxos/config";
//#region src/extensions/posthog/llm-analytics.ts
/**
* PostHog's LLM analytics schema. Three events with `$ai_*` properties are what the product reads:
* `$ai_trace` is the top-level unit (a conversation turn here), `$ai_span` a step inside it (a tool
* call), `$ai_generation` a model call. They are linked by `$ai_trace_id`, and a span or generation
* names its parent with `$ai_parent_id`.
*
* `$ai_input_tokens` is the uncached count, which is what PostHog expects alongside the two cache
* figures — together they price the call, and their ratio is the prompt-cache hit rate.
*/
var AI_GENERATION_EVENT = "$ai_generation";
var AI_TRACE_EVENT = "$ai_trace";
var AI_SPAN_EVENT = "$ai_span";
/** `$ai_trace`: the turn, carrying the prompt and the messages it produced as its states. */
var toAiTraceProperties = (turn) => stripUndefined({ ...spanProperties(turn) });
/** `$ai_span` of type `tool`: one tool call, carrying its arguments and result as its states. */
var toAiSpanProperties = (toolCall) => stripUndefined({
	...spanProperties(toolCall),
	$ai_span_type: "tool"
});
var spanProperties = (span) => ({
	$ai_trace_id: span.traceId,
	$ai_span_id: span.spanId,
	$ai_parent_id: span.parentSpanId,
	$ai_span_name: span.spanName,
	$ai_session_id: span.sessionId,
	$ai_latency: span.latency,
	$ai_input_state: span.content?.input,
	$ai_output_state: span.content?.output,
	$ai_content_truncated: span.content?.truncated,
	$ai_is_error: span.errorClass ? true : void 0,
	$ai_error: span.errorClass
});
var toAiGenerationProperties = (inference) => stripUndefined({
	$ai_trace_id: inference.traceId,
	$ai_span_id: inference.spanId,
	$ai_parent_id: inference.parentSpanId,
	$ai_span_name: inference.spanName,
	$ai_provider: inference.provider,
	$ai_model: inference.model,
	$ai_session_id: inference.sessionId,
	$ai_model_parameters: inference.parameters,
	$ai_input_tokens: inference.inputTokens,
	$ai_output_tokens: inference.outputTokens,
	$ai_cache_read_input_tokens: inference.cacheReadTokens,
	$ai_cache_creation_input_tokens: inference.cacheWriteTokens,
	$ai_latency: inference.latency,
	$ai_stream: inference.streaming,
	$ai_input: inference.content?.input,
	$ai_output_choices: inference.content?.output,
	$ai_tools: inference.content?.tools,
	$ai_content_truncated: inference.content?.truncated,
	$ai_is_error: inference.errorClass ? true : void 0,
	$ai_error: inference.errorClass
});
var stripUndefined = (properties) => Object.fromEntries(Object.entries(properties).filter(([, value]) => value !== void 0));
//#endregion
//#region src/version.ts
var DXOS_VERSION = "0.14.0";
//#endregion
//#region src/extensions/posthog/otel-destination.ts
var __dxlog_file$1 = "/__w/dxos/dxos/packages/sdk/observability/src/extensions/posthog/otel-destination.ts";
/** PostHog serves OTLP under `/i` of the same host it ingests product analytics on, not at the root. */
var OTLP_PATH_PREFIX = "/i";
/**
* PostHog as an OTLP backend, authenticated with the public `phc_` project token rather than a
* secret, so the browser can post to it directly.
*/
var otelDestination = (config) => {
	const host = getEnvString(config, "DX_POSTHOG_API_HOST");
	const apiKey = getEnvString(config, "DX_POSTHOG_API_KEY");
	if (!host) return;
	if (!apiKey) {
		log.info("DX_POSTHOG_API_HOST is set without DX_POSTHOG_API_KEY; skipping PostHog OTLP export", void 0, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file$1,
			L: 24,
			S: void 0
		});
		return;
	}
	return {
		endpoint: `${host.replace(/\/$/, "")}${OTLP_PATH_PREFIX}`,
		headers: { Authorization: `Bearer ${apiKey}` }
	};
};
//#endregion
//#region src/extensions/posthog/extension.ts
var __dxlog_file = "/__w/dxos/dxos/packages/sdk/observability/src/extensions/posthog/extension.ts";
var WIDGET_SESSION_STORAGE_KEY = "dxos.support.widgetSessionId";
var widgetSessionId = () => {
	try {
		const existing = localStorage.getItem(WIDGET_SESSION_STORAGE_KEY);
		if (existing) return existing;
		const minted = crypto.randomUUID();
		localStorage.setItem(WIDGET_SESSION_STORAGE_KEY, minted);
		return minted;
	} catch {
		return crypto.randomUUID();
	}
};
/** Same-origin route of the web deployment, which proxies the upload to object storage. */
var DEFAULT_FEEDBACK_LOGS_ENDPOINT = "/api/feedback-logs";
/** Upload serialized logs, gzipped, to the feedback-logs endpoint. Returns the R2 key on success. */
var uploadLogs = async (endpoint, ndjson, kind = "logs") => {
	try {
		const url = kind === "logs" ? endpoint : `${endpoint}${endpoint.includes("?") ? "&" : "?"}kind=${kind}`;
		const response = await fetch(url, {
			method: "POST",
			headers: { "Content-Type": "application/gzip" },
			body: await gzip(ndjson)
		});
		if (!response.ok) {
			log.warn("feedback log upload failed", {
				endpoint,
				status: response.status
			}, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 101,
				S: void 0
			});
			return;
		}
		const { key } = await response.json();
		return key;
	} catch (err) {
		log.warn("feedback log upload error", {
			endpoint,
			error: err
		}, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 107,
			S: void 0
		});
		return;
	}
};
/** Create a PostHog-backed observability extension for events, errors, and support tickets. */
var extensions = Effect.fn(function* ({ config, release, environment, serviceName = "app", posthog: posthogConfig, logStore, feedbackLogMaxSize, feedbackLogsEndpoint = DEFAULT_FEEDBACK_LOGS_ENDPOINT, node }) {
	if (isNode()) {
		const { extensions: nodeExtensions } = yield* Effect.promise(() => import("#posthog-transport"));
		return yield* nodeExtensions({
			config,
			release,
			environment,
			node
		});
	}
	if (typeof window === "undefined") {
		log("PostHog is being stubbed because it is running in a worker.", void 0, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 130,
			S: this
		});
		return stubExtension;
	}
	const apiKey = getEnvString(config, "DX_POSTHOG_API_KEY");
	const api_host = getEnvString(config, "DX_POSTHOG_API_HOST");
	if (!apiKey || !api_host) {
		log.info("Missing POSTHOG_API_KEY or POSTHOG_API_HOST", void 0, {
			"~LogMeta": "~LogMeta",
			F: __dxlog_file,
			L: 137,
			S: this
		});
		return stubExtension;
	}
	const { default: posthog } = yield* Effect.promise(() => import("posthog-js"));
	const { logProcessor } = yield* Effect.promise(() => import("./chunk-log-processor.mjs"));
	let unregisterPosthogProcessors;
	return {
		initialize: (context) => Effect.sync(() => {
			posthog.init(apiKey, {
				api_host,
				mask_all_text: true,
				capture_exceptions: true,
				cross_subdomain_cookie: false,
				...posthogConfig
			});
			posthog.register({
				sdkVersion: DXOS_VERSION,
				...release ? { release } : {},
				...environment ? { environment } : {}
			});
			unregisterPosthogProcessors?.();
			const removePosthogLog = log.addProcessor(logProcessor, void 0, {
				"~LogMeta": "~LogMeta",
				F: __dxlog_file,
				L: 163,
				S: this
			});
			const tagSession = () => context.setTags({ sessionId: posthog.get_session_id() }, "logs");
			const removeSessionListener = posthog.onSessionId(tagSession);
			tagSession();
			unregisterPosthogProcessors = () => {
				removePosthogLog();
				removeSessionListener();
			};
		}),
		close: () => Effect.sync(() => {
			unregisterPosthogProcessors?.();
			unregisterPosthogProcessors = void 0;
		}),
		enable: () => Effect.sync(() => posthog.opt_in_capturing()),
		disable: () => Effect.sync(() => posthog.opt_out_capturing()),
		identify: (distinctId, attributes, setOnceAttributes) => {
			posthog.identify(distinctId, attributes, setOnceAttributes);
		},
		alias: (distinctId, previousId) => {
			posthog.alias(distinctId, previousId);
		},
		setTags: (tags) => {
			posthog.register_for_session(tags);
		},
		get enabled() {
			return posthog.is_capturing();
		},
		apis: [
			{
				kind: "events",
				isAvailable: () => Effect.succeed(true),
				captureEvent: (event, attributes) => {
					posthog.capture(event, attributes);
				}
			},
			{
				kind: "errors",
				isAvailable: () => Effect.succeed(true),
				captureException: (error, attributes) => {
					posthog.captureException(error, attributes);
				}
			},
			{
				kind: "ai",
				isAvailable: () => Effect.succeed(true),
				captureInference: (inference) => {
					posthog.capture(AI_GENERATION_EVENT, toAiGenerationProperties(inference));
				},
				captureTurn: (turn) => {
					posthog.capture(AI_TRACE_EVENT, toAiTraceProperties(turn));
				},
				captureToolCall: (toolCall) => {
					posthog.capture(AI_SPAN_EVENT, toAiSpanProperties(toolCall));
				}
			},
			{
				kind: "support",
				isAvailable: () => Effect.succeed(logStore !== void 0),
				uploadLogs: async () => {
					if (logStore === void 0) return;
					const ndjson = await logStore.export({ maxSize: feedbackLogMaxSize });
					if (ndjson.length === 0) return;
					return await uploadLogs(feedbackLogsEndpoint, ndjson);
				},
				uploadNdjson: async (ndjson, kind) => ndjson.length === 0 ? void 0 : await uploadLogs(feedbackLogsEndpoint, ndjson, kind),
				sessionContext: () => {
					if (!posthog.__loaded) return;
					try {
						return {
							distinctId: posthog.get_distinct_id(),
							widgetSessionId: widgetSessionId(),
							sessionId: posthog.get_session_id(),
							replayUrl: posthog.get_session_replay_url({
								withTimestamp: true,
								timestampLookBack: 30
							}),
							currentUrl: `${window.location.origin}${window.location.pathname}`
						};
					} catch (err) {
						log.warn("PostHog session context unavailable", { err }, {
							"~LogMeta": "~LogMeta",
							F: __dxlog_file,
							L: 249,
							S: this
						});
						return;
					}
				},
				flushLogs: async (attributes) => {
					const destination = otelDestination(config);
					if (!destination) return;
					const ndjson = await logStore?.export({ maxSize: feedbackLogMaxSize });
					if (!ndjson || ndjson.length === 0) return;
					const { flushSupportLogs } = await import("./chunk-support-logs.mjs");
					const count = await flushSupportLogs(ndjson, {
						destination,
						resourceAttributes: {
							"service.name": serviceName,
							...release ? { "service.version": release } : {},
							...environment ? { "deployment.environment": environment } : {}
						},
						attributes
					});
					log.info("support logs flushed to PostHog", {
						...attributes,
						count
					}, {
						"~LogMeta": "~LogMeta",
						F: __dxlog_file,
						L: 272,
						S: this
					});
				}
			}
		]
	};
});
//#endregion
//#region src/extensions/posthog/index.ts
var posthog_exports = /* @__PURE__ */ __exportAll({
	AI_GENERATION_EVENT: () => AI_GENERATION_EVENT,
	AI_SPAN_EVENT: () => AI_SPAN_EVENT,
	AI_TRACE_EVENT: () => AI_TRACE_EVENT,
	extensions: () => extensions,
	otelDestination: () => otelDestination,
	toAiGenerationProperties: () => toAiGenerationProperties,
	toAiSpanProperties: () => toAiSpanProperties,
	toAiTraceProperties: () => toAiTraceProperties
});
//#endregion
//#region src/ObservabilityExtension.ts
var ObservabilityExtension_exports = /* @__PURE__ */ __exportAll({
	Otel: () => otel_exports,
	PostHog: () => posthog_exports,
	UNKNOWN_MCP_CLIENT: () => UNKNOWN_MCP_CLIENT
});
/** The client name an MCP event carries when no request named its client; `@posthog/mcp` drops a falsy one. */
var UNKNOWN_MCP_CLIENT = "unknown";
//#endregion
export { DXOS_VERSION as i, UNKNOWN_MCP_CLIENT as n, posthog_exports as r, ObservabilityExtension_exports as t };

//# sourceMappingURL=chunk-ObservabilityExtension.mjs.map